import React, { useEffect, useRef, useState } from 'react';
import Tooltip from './Tooltip';
import { cn } from './utils/classname';

interface OverflowTextProps extends React.HTMLAttributes<HTMLDivElement> {
  text: string;
  className?: string;
  truncateType?: 'center' | 'end';
  placement?: 'top' | 'right' | 'bottom' | 'left';
}

export const OverflowText = React.memo((props: OverflowTextProps) => {
  const {
    text = '',
    className = '',
    truncateType = 'end',
    placement = 'bottom',
    onClick,
    ...rest
  } = props;

  const [isOverflowed, setIsOverflow] = useState(false);
  const textElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = textElementRef.current;
    if (!element) return;

    const checkOverflow = () => {
      const isOverflow = element.scrollWidth > element.clientWidth;
      setIsOverflow(prev => (prev !== isOverflow ? isOverflow : prev));
    };

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(element);
    checkOverflow(); // Initial check

    return () => resizeObserver.disconnect();
  }, []);

  if (truncateType === 'end') {
    return (
      <Tooltip
        message={isOverflowed ? text : ''}
        position={placement}
      >
        <div
          ref={textElementRef}
          className={cn('truncate', className)}
          onClick={onClick}
          {...rest}
        >
          {text}
        </div>
      </Tooltip>
    );
  }

  const start = text.slice(0, text.length / 2);
  const end = text.slice(text.length / 2);

  return (
    <Tooltip
      message={isOverflowed ? text : ''}
      position={placement}
    >
      <div
        ref={textElementRef}
        className={cn('overflow-hidden', className)}
        onClick={onClick}
        {...rest}
      >
        <div className="flex">
          <div className="truncate">{start}</div>
          <div className="overflow-hidden whitespace-nowrap">{end}</div>
        </div>
      </div>
    </Tooltip>
  );
});

OverflowText.displayName = 'OverflowText';
